// src/contexts/PlayerModalContext.js
import React, { createContext, useContext, useState } from 'react';
import PlayerProfileModal from '../components/PlayerProfileModal';
import PositionAssignmentDropdown from '../components/league-dashboard/PositionAssignmentDropdown';
import RosterPlanModal from '../components/league-dashboard/RosterPlanModal';
import DropDesignationModal from '../components/modals/DropDesignationModal';
import { useRosterPlan } from '../hooks/rosterPlan';
import { analyzeRosterCapacity, canAddPlayers } from '../utils/RosterCapacityUtils';
import { leaguesAPI } from '../services/apiService';
import { formatApiError } from '../services/errorUtils';
import { useCommissioner } from './CommissionerContext';
import { getWaiverActionState } from '../utils/waiverStatus';
import { useWaiverClaimFlow } from '../hooks/useWaiverClaimFlow';

export const PlayerModalContext = createContext();

export const usePlayerModal = () => {
  const context = useContext(PlayerModalContext);
  if (!context) {
    throw new Error('usePlayerModal must be used within a PlayerModalProvider');
  }
  return context;
};

export const PlayerModalProvider = ({ children, leagueId, userTeamId, league = null }) => {
  // SSOT: commissioner MODE (toggle state) comes from CommissionerContext —
  // never from the caller's role. Role ≠ mode.
  const { isCommissionerMode, getTargetTeamId } = useCommissioner();
  const [modalState, setModalState] = useState({
    isOpen: false,
    playerId: null,
    playerData: null
  });

  // Position assignment state (mirrors FreeAgentSearch pattern)
  const [positionModalState, setPositionModalState] = useState({
    isOpen: false,
    player: null,
    currentRoster: []
  });

  // Chunk 3.16 Bug 1: proactive roster-capacity gate + drop designation,
  // mirroring FreeAgentSearch.js:252-264. When the roster is full on TOTAL
  // capacity, the player-profile ADD must surface the drop/rearrange prompt
  // (DropDesignationModal) BEFORE position assignment — same as the FA-list
  // add. (The §4.5.20 onOpenDropShuffle CTA only fires reactively when a
  // single position is full; it does not cover the total-capacity case.)
  const [dropModalState, setDropModalState] = useState({
    isOpen: false,
    player: null,
    currentRoster: []
  });
  const [designatedDropId, setDesignatedDropId] = useState(null);

  // Bug 1 follow-up (Bible § 4.5.20, 2026-05-15): drop-shuffle plan flow
  // for the player-profile-modal ADD button. Same canonical wiring as
  // FreeAgentSearch + LeagueDashboard. Pre-fix, clicking ADD on a FA
  // pitcher with full active P slots showed the warning panel WITHOUT
  // the prominent "Drop a Player to Make Room" CTA (since this caller
  // didn't pass onOpenDropShuffle).
  const rosterPlan = useRosterPlan({
    leagueId,
    candidate: positionModalState.player,
    currentRoster: positionModalState.currentRoster,
    isCommissionerMode,
    getTargetTeamId,
  });

  // DD-WAIVER-0723-BUILD W2: waiver-claim flow for the player-profile ADD.
  // An on-waivers player must never enter the fa_add capacity gate or
  // position assignment — the claim branch in handlePlayerAction('add')
  // fires first and routes here (same submit flow as FA-search).
  const waiverClaim = useWaiverClaimFlow({
    leagueId,
    league,
    onSuccess: (claimedPlayer, message) => { alert(message); },
    onError: (message) => { alert(message); },
  });

  const openPlayerModal = (playerId, playerData = null) => {
    setModalState({
      isOpen: true,
      playerId: playerId,
      playerData: playerData
    });
  };

  const closePlayerModal = () => {
    setModalState({
      isOpen: false,
      playerId: null,
      playerData: null
    });
  };

  // Execute the add-player API call after the user picks a position
  const handlePositionAssigned = async (assignmentData) => {
    const player = assignmentData.player;
    const assignment = {
      position: assignmentData.assignment === 'bench' ? 'bench' :
                assignmentData.assignment === 'minors' ? 'minors' :
                assignmentData.assignment.split('_')[0],
      slotId: assignmentData.assignment,
      type: assignmentData.roster_status,
    };

    try {
      const leaguePlayerId = player.ids?.league_player;

      // Bible Rule 9: missing required data is a bug — throw, not fallback.
      if (!leaguePlayerId) {
        throw new Error(
          `Cannot add player: missing ids.league_player (mlb=${player.ids?.mlb}, ` +
          `name=${player.info?.full_name}). Player object likely came from non-canonical source.`
        );
      }
      if (!assignment?.type) {
        throw new Error('Cannot add player: assignment.type is required');
      }
      if (assignment.type === 'active' && !assignment.slotId) {
        throw new Error(
          `Cannot add ${player.info?.full_name} to active roster: missing assignment.slotId`
        );
      }

      // 4-branch contract resolution per Bible § 4.5 contract model:
      //   - Minors: zero out
      //   - Offseason w/ projected_contract (line 1027): keeper rights
      //   - Owned in-season: existing roster_data.financial
      //   - FA pickup (default): baseline_price + league policy (§ 4.5.1 line 809, § 4.5.2 line 821)
      const isOffseason = ['setup', 'draft', 'finalizing'].includes(league?.league_status);
      const isOwnedAlready = player.league_context?.status === 'owned';
      const isMinors = assignment.type === 'minors';
      const playerName = player.info?.full_name;

      let salary;
      let contractYears;
      if (isMinors) {
        // 9g.2: Per Tony's C1 canonical rule, minors players are price-null.
        // Backend 9g.1 minors-aware branch preserves null state in JSONB.
        // Contract is created at promote_from_minors (9f.1), not at add time.
        // Previous hardcoded 0/0 was Pattern-B class same as 8m.6a/8m.8a.
        salary = null;
        contractYears = null;
      } else if (isOffseason && player.projected_contract) {
        salary = player.projected_contract.baseline_price;
        contractYears = player.projected_contract.contract_years;
        if (salary == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
        }
      } else if (isOwnedAlready) {
        salary = player.league_context.financial?.contract_salary;
        contractYears = player.league_context.financial?.contract_years;
        if (salary == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`);
        }
        if (contractYears == null) {
          throw new Error(`Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`);
        }
      } else {
        // Bible § 4.5.1 line 809 + § 10 line 1701: FA pickup price = baseline_price.
        // Bible § 4.5.2 line 821: contract_years = league.salary_settings.standard_contract_length.
        salary = player.league_context?.financial?.baseline_price;
        contractYears = league?.salary_settings?.standard_contract_length;
        if (salary == null) {
          throw new Error(
            `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
            `Pricing engine may not have run for this league.`
          );
        }
        if (contractYears == null) {
          throw new Error(
            `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`
          );
        }
      }

      const payload = {
        league_player_id: leaguePlayerId,
        salary,
        contract_years: contractYears,
        roster_status: assignment.type,
        roster_position: assignment.type === 'active' ? assignment.slotId : null,
        // Bug 1: when the roster was full, the user designated a drop via
        // DropDesignationModal — release it as part of this add.
        drop_player_id: designatedDropId || null
      };

      if (isCommissionerMode) {
        payload.commissioner_action = true;
        payload.target_team_id = getTargetTeamId(userTeamId);
      }

      const response = await leaguesAPI.addPlayerToTeam(leagueId, payload);

      if (response.success) {
        const name = player.info?.full_name || 'Player';
        const posText = assignment.type === 'active' ? assignment.position.toUpperCase() : assignment.type;
        alert(`${name} added to team (${posText})!`);
        window.dispatchEvent(new Event('roster-updated'));
      } else {
        alert(response.message || 'Failed to add player');
      }
    } catch (err) {
      // DD-NIMMO-422-FE: surface the server's message. The DynastyDugout error
      // envelope is { error: { message } }, NOT { detail } — reading only
      // .detail/.message swallowed real reasons (e.g. the DL-residency lock)
      // and showed "Request failed with status code 422". formatApiError reads
      // all three backend envelopes.
      alert(formatApiError(err, 'Failed to add player'));
    } finally {
      setPositionModalState({ isOpen: false, player: null, currentRoster: [] });
      setDesignatedDropId(null);
    }
  };

  const handlePositionCancelled = () => {
    setPositionModalState({ isOpen: false, player: null, currentRoster: [] });
    setDesignatedDropId(null);
  };

  const handlePlayerAction = async (action, playerData) => {
    console.log(`Player action: ${action}`, playerData);

    try {
      switch (action) {
        case 'add': {
          // W2 waiver gate (Bible § 4.5.19) — fires BEFORE the roster fetch,
          // the fa_add capacity gate, and position assignment. Cooling-off
          // (viewer's team cut the player, hold still active) surfaces as an
          // action error in the profile modal; an active hold from another
          // team routes into the waiver-claim flow. Post-expiry falls
          // through to the normal ADD.
          const actingTeamId = isCommissionerMode ? getTargetTeamId(userTeamId) : userTeamId;
          const waiverState = getWaiverActionState(playerData, actingTeamId || null);
          if (waiverState.onWaivers && !waiverState.expired && waiverState.coolingOff) {
            return {
              success: false,
              message: 'Cooling Off — you cut this player; wait for waivers to clear.',
            };
          }

          // Fetch current roster for position assignment dropdown
          const rosterResponse = isCommissionerMode && userTeamId
            ? await leaguesAPI.getTeamRosterCanonical(leagueId, userTeamId)
            : await leaguesAPI.getMyRosterCanonical(leagueId);

          const players = rosterResponse?.players || [];
          const activePlayers = players.filter(p => {
            const status = p.league_context?.roster?.status;
            return status && status !== 'former' && status !== 'inactive';
          });

          // Close player profile modal first.
          closePlayerModal();

          // W2: active waiver hold → claim modal (drop designation optional),
          // never position assignment for a waiver-held player.
          if (waiverState.onWaivers && !waiverState.expired) {
            waiverClaim.openClaim(playerData, {
              currentRoster: activePlayers,
              capacityAnalysis: analyzeRosterCapacity(league, activePlayers),
            });
            return { cancelled: true };
          }

          // Bug 1: proactive total-capacity gate (mirrors FreeAgentSearch.js).
          // analyzeRosterCapacity needs league.roster_settings.position_requirements;
          // when that's unavailable (league not passed to this provider) the
          // analysis is null and we fall through to the position modal — which
          // still surfaces the §4.5.20 per-position drop CTA, so no regression.
          const capacityAnalysis = analyzeRosterCapacity(league, activePlayers);
          if (capacityAnalysis && !canAddPlayers(1, capacityAnalysis).canAdd) {
            setDesignatedDropId(null);
            setDropModalState({
              isOpen: true,
              player: playerData,
              currentRoster: activePlayers
            });
            return { cancelled: true };
          }

          // Capacity available (or unknown) → open position assignment.
          setPositionModalState({
            isOpen: true,
            player: playerData,
            currentRoster: activePlayers
          });

          return { cancelled: true };
        }

        case 'drop': {
          const dropName = playerData.info?.full_name || `${playerData.info?.first_name || ''} ${playerData.info?.last_name || ''}`.trim() || 'this player';
          const dropLeaguePlayerId = playerData.ids?.league_player;
          if (!dropLeaguePlayerId) {
            return { success: false, error: 'Player ID not found. Please refresh and try again.' };
          }
          if (!window.confirm(`Drop ${dropName}?`)) {
            return { success: false, cancelled: true };
          }

          const dropOptions = {};
          if (isCommissionerMode) {
            dropOptions.commissioner_action = true;
            dropOptions.target_team_id = getTargetTeamId(userTeamId);
          }

          const dropResponse = await leaguesAPI.dropPlayerFromTeam(
            leagueId,
            dropLeaguePlayerId,
            dropOptions
          );

          if (dropResponse.success) {
            alert(`${dropName} dropped!`);
            return { success: true };
          } else {
            return { success: false, message: dropResponse.message || 'Failed to drop player' };
          }
        }

        case 'trade': {
          const tradeName = playerData.info?.full_name || `${playerData.info?.first_name || ''} ${playerData.info?.last_name || ''}`.trim() || 'Player';
          alert(`Trade functionality for ${tradeName} - Coming Soon!`);
          return { success: false, cancelled: true };
        }

        default:
          console.log('Unknown action:', action);
          return { success: false, message: `Unknown action: ${action}` };
      }
    } catch (err) {
      console.error(`Error ${action} player:`, err);
      // DD-NIMMO-422-FE: surface the server's message (DL-residency lock,
      // capacity, eligibility) instead of the generic axios status string.
      const errorMessage = formatApiError(err, `Failed to ${action} player`);
      return { success: false, message: errorMessage };
    }
  };

  return (
    <PlayerModalContext.Provider
      value={{
        openPlayerModal,
        closePlayerModal,
        ...modalState
      }}
    >
      {children}
      
      <PlayerProfileModal
        playerId={modalState.playerId}
        leagueId={leagueId}
        isOpen={modalState.isOpen}
        onClose={closePlayerModal}
        userTeamId={userTeamId}
        onPlayerAction={handlePlayerAction}
        isCommissionerMode={isCommissionerMode}
      />

      {/* Bug 1: proactive drop-designation when roster is full — mirrors
          the FA-list (FreeAgentSearch.js DropDesignationModal). Designate a
          release, then continue to position assignment with drop_player_id. */}
      <DropDesignationModal
        isOpen={dropModalState.isOpen}
        onClose={() => {
          setDropModalState({ isOpen: false, player: null, currentRoster: [] });
          setDesignatedDropId(null);
        }}
        onConfirm={(dropPlayerId) => {
          setDesignatedDropId(dropPlayerId);
          const pendingPlayer = dropModalState.player;
          const pendingRoster = dropModalState.currentRoster;
          setDropModalState({ isOpen: false, player: null, currentRoster: [] });
          setPositionModalState({
            isOpen: true,
            player: pendingPlayer,
            currentRoster: pendingRoster
          });
        }}
        playerBeingAdded={dropModalState.player}
        currentRoster={dropModalState.currentRoster}
        leagueId={leagueId}
        context="fa_add"
        isRosterFull={true}
        // W2: real value from the player's canonical availability namespace
        // (was hardcoded null). Waiver priority is a league-level fact, not
        // on the player object — FA-search parity passes null.
        waiverPriority={null}
        totalTeams={null}
        waiverExpiresAt={dropModalState.player?.league_context?.availability?.waiver_expires_at || null}
      />

      {/* W2: waiver-claim modal (context='waiver_claim') — same submit flow
          as FA-search (leaguesAPI.submitWaiverClaim via useWaiverClaimFlow). */}
      <DropDesignationModal {...waiverClaim.modalProps} />

      <PositionAssignmentDropdown
        player={positionModalState.player}
        league={league}
        currentRoster={positionModalState.currentRoster}
        onAssign={handlePositionAssigned}
        onCancel={handlePositionCancelled}
        isVisible={positionModalState.isOpen}
        onOpenDropShuffle={() => {
          // Close the position-assignment modal but KEEP the
          // positionModalState (candidate + currentRoster) alive so the
          // RosterPlanModal that pops next has the same data in scope.
          // handlePositionCancelled would wipe state — don't call it.
          setPositionModalState((prev) => ({ ...prev, isOpen: false }));
          rosterPlan.open();
        }}
      />

      {/* RosterPlanModal — drop-shuffle for player-profile ADD button.
          Bible § 4.5.10 atomic drop+add via /plan-add. Wired
          2026-05-15 as part of the § 4.5.20 invariant rollout. */}
      <RosterPlanModal
        candidate={positionModalState.player}
        currentRoster={positionModalState.currentRoster}
        state={rosterPlan.state}
        plans={rosterPlan.plans}
        selectedDropPlayer={rosterPlan.selectedDropPlayer}
        selectedPlan={rosterPlan.selectedPlan}
        errorClass={rosterPlan.errorClass}
        errorMessage={rosterPlan.errorMessage}
        infeasibleReason={rosterPlan.infeasibleReason}
        onPickDrop={rosterPlan.requestPlans}
        onChangeDrop={rosterPlan.changeDrop}
        onSelectPlan={rosterPlan.selectPlan}
        onConfirm={rosterPlan.executePlan}
        onCancel={() => {
          rosterPlan.cancel();
          handlePositionCancelled();
        }}
        isVisible={rosterPlan.state !== 'idle' && rosterPlan.state !== 'cancelled'}
      />
    </PlayerModalContext.Provider>
  );
};

// Convenience hook for easy usage
export const useShowPlayerProfile = () => {
  const { openPlayerModal } = usePlayerModal();
  
  return (player) => {
    const playerId = player.ids?.mlb;
    openPlayerModal(playerId, player);
  };
};